import { css } from '@emotion/react';
import { getLineItemRowChangeOrders, getScanWithRevertedLineItem, LineItemChangeOrder } from '@kaiizen/kai-scan-template-system';
import React, { useCallback, useState } from 'react';

import { getLineItemFieldChanges, type NamedModifier, presentLineItemTotal, type ScanPartFieldChange } from '@/Client/Presenters';
import { type ScanEditingContextType, useOrgProduct, useScanEditingContextSelector, useScanEditingContextSlice } from '@/Client/State';
import { useConfirmationModal } from '@/Client/State/ConfirmationModalContext';
import { useToast } from '@/Client/State/ToastContext';
import { LAYOUT2 } from '@/Client/Styles';
import { styleguideColors as COLORS, styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT, styleguideLetterSpacing as TRACKING } from '@kaiizen/kai-shiki/core';

import { FocusedLineItemProductPhoto } from './FocusedLineItemProductPhoto';
import { FocusedLineItemStrip, FocusedLineItemStripButton, FocusedLineItemStripMessage } from './FocusedLineItemStrip';
import { FocusedLineItemTab } from './FocusedLineItemTab';
import { FocusedOrgProductFlyout } from './FocusedOrgProductFlyout';
import { FOCUSED_LINE_ITEM_TAB_ERROR } from './focusedTaskCardStyles';

export const CANCEL_LINE_ITEM_CHANGES_LABEL = 'Cancel line item changes';
const CANCEL_LINE_ITEM_CHANGES_CONFIRM_TITLE = 'Cancel Line Item Changes';
export const CANCEL_LINE_ITEM_CHANGES_CONFIRM_MESSAGE = 'Are you sure you want to cancel the changes to this line item?';

/** Where a line item row sits: its task, its option and line item templates, and its line item. */
type LineItemRowLocation = {
  groupId: string;
  taskId: string;
  optionTemplateId: string;
  lineItemTemplateId: string;
  /** The row's line item, when it's in the scan; undefined for its template's preview row. */
  lineItemId: string | undefined;
};

/** A change-order strip's field name ("Qty"), leading what moved on it: small mono capitals, midtone. */
const FOCUSED_LINE_ITEM_STRIP_FIELD = css`
  label: FOCUSED_LINE_ITEM_STRIP_FIELD;
  margin-right: ${LAYOUT2.spacingHalf}px;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(FONT_SIZE.monoMicro)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${TRACKING.monoCaption};
  text-transform: uppercase;
  color: ${COLORS.midtone};
`;

/** A product a change-order entry names: its photo and name in a row, a button opening its details. */
const CHANGE_ORDER_PRODUCT = css`
  label: CHANGE_ORDER_PRODUCT;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  vertical-align: middle;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

/** A product's name in a change-order entry, cut short: product names run long, and the full name is its tooltip. */
const CHANGE_ORDER_PRODUCT_NAME = css`
  label: CHANGE_ORDER_PRODUCT_NAME;
  max-width: 140px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const NO_LINE_ITEM_CHANGE_ORDERS: LineItemChangeOrder[] = [];

/** What the change order did to the line items behind a line item row; none while the scan isn't a change order. */
const getRowChangeOrders = (
  { showScanChanges, scanRoot, baselineScanRoot }: Pick<ScanEditingContextType, 'showScanChanges' | 'scanRoot' | 'baselineScanRoot'>,
  { groupId, taskId, optionTemplateId, lineItemTemplateId, lineItemId }: LineItemRowLocation,
): LineItemChangeOrder[] =>
  showScanChanges && baselineScanRoot
    ? getLineItemRowChangeOrders(
      scanRoot?.groupsById[groupId]?.tasksById[taskId],
      baselineScanRoot.groupsById[groupId]?.tasksById[taskId],
      optionTemplateId,
      lineItemTemplateId,
      lineItemId,
    )
    : NO_LINE_ITEM_CHANGE_ORDERS;

const getLocationDeps = ({ groupId, taskId, optionTemplateId, lineItemTemplateId, lineItemId }: LineItemRowLocation) => [
  groupId,
  taskId,
  optionTemplateId,
  lineItemTemplateId,
  lineItemId,
];

/**
 * The ids of the line items a change order is about: the one now, the one approved, or both. A replacement is about
 * both, so cancelling it takes the new line item out and puts the approved one back.
 */
const getChangeOrderLineItemIds = (lineItemChangeOrder: LineItemChangeOrder) => [
  ...(lineItemChangeOrder.kind === 'removed' ? [] : [lineItemChangeOrder.lineItem.id]),
  ...(lineItemChangeOrder.kind === 'added' ? [] : [lineItemChangeOrder.baselineLineItem.id]),
];

/** Whether the change order touched the line items behind a line item row. */
export const useLineItemRowHasChangeOrder = (location: LineItemRowLocation): boolean =>
  useScanEditingContextSelector(context => getRowChangeOrders(context, location).length > 0, getLocationDeps(location));

/** One thing a change-order strip records, keyed among its others. */
type ChangeOrderEntryContent = ScanPartFieldChange & {
  key: string;
  /** On a product change, the approved product and the new one, so each can show its photo and details. */
  beforeSkuId?: string;
  afterSkuId?: string;
};

/** A field the change order cleared or first set shows a dash on its empty side; a modifier change is its name alone. */
const getChangeOrderFieldEntry = (lineItemId: string, change: ScanPartFieldChange): ChangeOrderEntryContent => ({
  key: `${lineItemId}-${change.isModifier ? 'modifier' : 'field'}-${change.field}`,
  ...change,
  ...(!change.isModifier && { before: change.before ?? '—', after: change.after ?? '—' }),
});

/**
 * A change order's entries: an added line its total, a removed one its approved total, and a changed one what moved on
 * it as the line item shows it (getLineItemFieldChanges).
 */
const getChangeOrderEntries = (lineItemChangeOrder: LineItemChangeOrder, knownTaskModifiers: readonly NamedModifier[]): ChangeOrderEntryContent[] => {
  if (lineItemChangeOrder.kind === 'added') {
    const { lineItem } = lineItemChangeOrder;
    return [{ key: `added-${lineItem.id}`, field: 'Added', after: presentLineItemTotal(lineItem) }];
  }
  if (lineItemChangeOrder.kind === 'removed') {
    const { baselineLineItem } = lineItemChangeOrder;
    return [{ key: `removed-${baselineLineItem.id}`, field: 'Removed', before: presentLineItemTotal(baselineLineItem) }];
  }
  const { lineItem, baselineLineItem } = lineItemChangeOrder;
  return getLineItemFieldChanges(baselineLineItem, lineItem, lineItem.template.values, knownTaskModifiers)
    .map(change => ({
      ...getChangeOrderFieldEntry(lineItem.id, change),
      ...(change.fieldKey === 'product' && { beforeSkuId: baselineLineItem.skuId, afterSkuId: lineItem.skuId }),
    }));
};

/**
 * A product a change-order entry names: its photo and its name, cut short, opening the product's details on a click.
 * The approved product's name is struck through.
 */
const ChangeOrderProduct = ({ skuId, name, approved }: { skuId: string; name: string; approved?: boolean }) => {
  const { orgProduct } = useOrgProduct(skuId);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const Name = approved ? 's' : 'b';
  return (
    <>
      <button
        css={CHANGE_ORDER_PRODUCT}
        type='button'
        title={name}
        disabled={!orgProduct}
        onClick={() => setDetailsOpen(true)}
        data-testid='focused-line-item-change-order-product'
      >
        <FocusedLineItemProductPhoto image={orgProduct?.image} compact />
        <Name css={CHANGE_ORDER_PRODUCT_NAME}>{name}</Name>
      </button>
      <FocusedOrgProductFlyout orgProduct={orgProduct ?? null} open={detailsOpen} onClose={() => setDetailsOpen(false)} />
    </>
  );
};

/** One thing the change order moved: the field, what it was (struck through), and what it is now. */
const ChangeOrderEntry = ({ field, before, after, beforeSkuId, afterSkuId }: Omit<ChangeOrderEntryContent, 'key'>) => (
  <FocusedLineItemStripMessage testId='focused-line-item-change-order-entry'>
    <span css={FOCUSED_LINE_ITEM_STRIP_FIELD}>{field}</span>
    {before && (beforeSkuId ? <ChangeOrderProduct skuId={beforeSkuId} name={before} approved /> : <s>{before}</s>)}
    {before && after && ' → '}
    {after && (afterSkuId ? <ChangeOrderProduct skuId={afterSkuId} name={after} /> : <b>{after}</b>)}
  </FocusedLineItemStripMessage>
);

/**
 * The strip under a line item row the change order touched: its tag, what moved on the line items behind the row, and
 * Cancel line item changes to put them back as approved. Approving is the change order's own, in the navigator.
 */
export const FocusedLineItemChangeOrderStrip = (location: LineItemRowLocation) => {
  const { groupId, taskId } = location;
  const { scanIsEditable, scanDataMutators, baselineScanRoot, knownTaskModifiers } = useScanEditingContextSlice([
    'scanIsEditable',
    'scanDataMutators',
    'baselineScanRoot',
    'knownTaskModifiers',
  ]);
  const confirm = useConfirmationModal();
  const { tryWithErrorToast } = useToast();
  const lineItemChangeOrders = useScanEditingContextSelector(context => getRowChangeOrders(context, location), getLocationDeps(location));

  const cancelLineItemChanges = useCallback(() => {
    if (!baselineScanRoot) return;
    const lineItemIds = new Set(lineItemChangeOrders.flatMap(getChangeOrderLineItemIds));
    confirm({
      title: CANCEL_LINE_ITEM_CHANGES_CONFIRM_TITLE,
      message: CANCEL_LINE_ITEM_CHANGES_CONFIRM_MESSAGE,
      onConfirm: () => {
        tryWithErrorToast(
          'cancel line item changes',
          () =>
            scanDataMutators.setScan(currentScan =>
              [...lineItemIds].reduce((scan, lineItemId) => getScanWithRevertedLineItem(scan, baselineScanRoot, groupId, taskId, lineItemId), currentScan)
            ),
        );
      },
    });
  }, [baselineScanRoot, lineItemChangeOrders, confirm, tryWithErrorToast, scanDataMutators, groupId, taskId]);

  return (
    <FocusedLineItemStrip
      testId='focused-line-item-change-order-strip'
      actions={scanIsEditable && <FocusedLineItemStripButton onClick={cancelLineItemChanges}>{CANCEL_LINE_ITEM_CHANGES_LABEL}</FocusedLineItemStripButton>}
    >
      <FocusedLineItemTab
        variantStyle={FOCUSED_LINE_ITEM_TAB_ERROR}
        testId='focused-line-item-change-order-tab'
        explanationTitle='Change Order'
        explanation='This line has changed since the scope was approved.'
        hint='Approving the change order applies the change.'
      >
        {'Change order'}
      </FocusedLineItemTab>
      {lineItemChangeOrders
        .flatMap(lineItemChangeOrder => getChangeOrderEntries(lineItemChangeOrder, knownTaskModifiers))
        .map(({ key, ...entry }) => <ChangeOrderEntry key={key} {...entry} />)}
    </FocusedLineItemStrip>
  );
};
